import PropTypes from "prop-types";
import { useEffect, useMemo } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
// material
import { styled } from "@mui/material/styles";
import { Box, Drawer, Typography } from "@mui/material";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
// components
import Logo from "../../components/Logo";
import Scrollbar from "../../components/Scrollbar";
import NavSection from "../../components/NavSection";
import { MHidden } from "../../components/@material-extend";
import sidebarConfig from "./SidebarConfig";
import { useAuth } from "src/hooks/useAuth.hook";
import { teamsSlice } from "src/store/teams/teams.slice";
import { useDispatch } from "react-redux";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { FEATURE } from "src/util/enums";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 250;

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

// const AccountStyle = styled("div")(({ theme }) => ({
//   display: "flex",
//   alignItems: "center",
//   padding: theme.spacing(2, 2.5),
//   borderRadius: theme.shape.borderRadiusSm,
//   backgroundColor: theme.palette.grey[200],
// }));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

const ITEM_HEIGHT = 28;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 100,
    },
  },
};

const notASideBarLink = ["Finance"]    //ARRAY OF FEATUREs NOT TO BE SHOWN AS A SIDEBAR LINK

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { teamId, team } = useSelectedTeam();
  const selectedTeamFromLS = JSON.parse(localStorage.getItem('selectedTeamId'))

  const company = useMemo(() => {
    return user?.role?.find((team) => team.teamId === user?.company?.id);
  }, [user]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    dispatch(teamsSlice.actions.setTeamId(+value));
    localStorage.setItem('selectedTeamId', JSON.stringify(value));
  };
  useEffect(() => {
    if (selectedTeamFromLS) {
      dispatch(teamsSlice.actions.setTeamId(selectedTeamFromLS));
    } else {
      team?.teamId ??
        dispatch(
          teamsSlice.actions.setTeamId(
            company?.teamId ?? user?.role?.[0]?.teamId ?? null
          )
        );
    }
  }, [])

  // useEffect(() => {
  //   team?.teamId ??
  //     dispatch(
  //       teamsSlice.actions.setTeamId(
  //         company?.teamId ?? user?.role?.[0]?.teamId ?? null
  //       )
  //     );
  // }, [dispatch, user, company, team?.teamId]);

  const navConfig = useMemo(() => {
    // Use team data which already has access permissions
    const currentTeam = team;

    // Return empty array if team or team.access is not available
    if (!currentTeam || !currentTeam.access || !Array.isArray(currentTeam.access)) {
      return [];
    }

    // Check if Grand Parent Company is selected (teamId matches user.company.id)
    const isGrandParentCompanySelected = String(teamId) === String(user?.company?.id);

    // Check if user has Finance FA access (grand parent company access)
    const financeAccess = currentTeam.access.find(
      (item) => item.featureName === "Finance"
    );
    const hasFinanceConfigAccess =
      financeAccess?.view === "Y" &&
      financeAccess?.create === "Y" &&
      financeAccess?.update === "Y" &&
      financeAccess?.delete === "Y";

    // Add ChargingSite feature if not available from backend
    const existingChargingSite = currentTeam.access.find(feature => feature.featureName === "ChargingSite");
    let updatedAccess = [...currentTeam.access];

    // if (!existingChargingSite) {
    //   const chargingSiteFeature = {
    //     create: "Y",
    //     delete: "Y",
    //     featureId: updatedAccess.length + 1,
    //     featureName: "ChargingSite",
    //     update: "Y",
    //     view: "Y"
    //   };
    //   updatedAccess = [...updatedAccess, chargingSiteFeature];
    // }

    const config = updatedAccess.reduce((finalConfig, item) => {
      if (item.view === "Y" && item.featureName && !notASideBarLink.includes(item.featureName)) {
        // Only show PaymentGateway if user has Finance FA access AND Grand Parent Company is selected
        if (item.featureName === "PaymentGateway" && (!hasFinanceConfigAccess || !isGrandParentCompanySelected)) {
          return finalConfig;
        }

        // Prevent generating an empty sidebar item if the feature name is not mapped in sidebarConfig
        if (!sidebarConfig[item.featureName]) {
          return finalConfig;
        }

        finalConfig = [...finalConfig, { ...sidebarConfig[item.featureName], access: item }];
      }
      return finalConfig;
    }, []);

    // Add PaymentGateway menu if user has Finance FA access AND Grand Parent Company is selected
    // even if it's not in the backend access array
    if (hasFinanceConfigAccess && isGrandParentCompanySelected && sidebarConfig[FEATURE.PAYMENTGATEWAY]) {
      const existingPaymentGateway = config.find(
        (item) => item.path === sidebarConfig[FEATURE.PAYMENTGATEWAY].path
      );
      if (!existingPaymentGateway) {
        config.push({
          ...sidebarConfig[FEATURE.PAYMENTGATEWAY],
          access: {
            featureName: "PaymentGateway",
            view: "Y",
            create: "Y",
            update: "Y",
            delete: "Y",
          },
        });
      }
    }

    // Sort sidebar items to match the fixed display order defined in sidebarConfig
    const sidebarOrder = Object.values(sidebarConfig).map((item) => item.path);
    config.sort((a, b) => {
      const indexA = sidebarOrder.indexOf(a.path);
      const indexB = sidebarOrder.indexOf(b.path);
      return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
    });

    return config;
  }, [team, teamId, user]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 3 }}>
        <Box component={RouterLink} to="/" sx={{ display: "inline-flex" }}>
          <Logo />
        </Box>
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <FormControl sx={{ m: 1 }}>
          <Select
            style={{ width: "204px", borderRadius: 4, height: "56px" }}
            value={team?.teamId ?? ""}
            onChange={handleChange}
            MenuProps={MenuProps}
          >
            <MenuItem
              key={user?.company?.id}
              value={user?.company?.id}
              sx={{
                fontWeight: 600,
                color: "var(--app-color)",
              }}
              disabled={!company}
            >
              {user?.company?.name ?? ""}
            </MenuItem>
            {user?.role
              ?.filter((team) => team.teamId !== user?.company?.id)
              ?.map((team) => (
                <MenuItem
                  key={team.teamId}
                  value={team.teamId}
                  sx={{
                    pl: 4,
                  }}
                >
                  {team.teamName}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {/* <Link underline="none" component={RouterLink} to="#">
          <AccountStyle>
            <Avatar src={account.photoURL} alt="photoURL" />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: "text.primary" }}>
                {userDetails.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {account.role}
              </Typography>
            </Box>
          </AccountStyle>
        </Link> */}
      </Box>

      <NavSection navConfig={navConfig} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        {/* <Stack
          alignItems="center"
          spacing={3}
          sx={{
            p: 2.5,
            pt: 5,
            borderRadius: 2,
            position: "relative",
            bgcolor: "grey.200",
          }}
        >
          <Box
            component="img"
            src="/static/illustrations/illustration_avatar.png"
            sx={{ width: 100, position: "absolute", top: -50 }}
          />

          <Box sx={{ textAlign: "center" }}>
            <Typography gutterBottom variant="h6">
              Get more?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              From only $69
            </Typography>
          </Box>

          <Button
            fullWidth
            href="https://material-ui.com/store/items/minimal-dashboard/"
            target="_blank"
            variant="contained"
          >
            Upgrade to Pro
          </Button>
        </Stack> */}
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.default",
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
